import * as React from "react";
import * as PopoverPrimitive from "@radix-ui/react-popover";
import { Check, ChevronDown, Loader2,Minus, Search } from "lucide-react";
import { SelectedLabel } from "./Dropdown";
import { cn } from "./ui/utils";

export interface MultiSelectOption {
  value: string;
  label: string;
  dot?: string;
  disabled?: boolean;
}

interface MultiSelectDropdownProps {
  onOpen?: () => void;
  leadingLabel?: string;
  options: MultiSelectOption[];
  /** Currently selected values. */
  selected: string[];
  onChange: (values: string[]) => void;
  placeholder?: string;
  disabled?: boolean;
  size?: "sm" | "md";
  className?: string;
  searchable?: boolean;
  onApply?: () => void;
  applyLabel?: string;
  onClearAll?: () => void;
  selectedNoun?: string;
  variant?: "pill" | "form";
  alwaysBelow?: boolean;
  visibleOptions?: number;
  loading?: boolean;
  /** Custom portal container. If not provided, automatically portals into the nearest dialog/sheet if inside one. */
  container?: HTMLElement | null;
  /** Opt-in "Select All" row above the options. Off by default so existing
   *  dropdowns are untouched. Honours the search box: with a query typed it
   *  selects (or clears) only the options currently listed. */
  selectAll?: boolean;
  selectAllLabel?: string;
  /**
   * Collapses a multi-pick selection to a count badge beside the leading
   * label, and names the option outright when exactly one is picked.
   *
   * Opt-in, because it changes what the trigger says: the nine existing
   * `selectedNoun` callers keep their current wording untouched.
   */
  countBadge?: boolean;
  /**
   * Replaces the built-in summary trigger. For callers that need the same
   * search + select-all + checkbox panel behind a control of their own -- a
   * column picker behind a "More Fields" button, say -- rather than behind a
   * dropdown showing "N selected".
   */
  trigger?: React.ReactNode;
}

const OPTION_ROW_PX = 32;
const LIST_PADDING_PX = 8;
const DEFAULT_LIST_MAX_PX = 240;

/* Options are clamped to the dropdown width, so a long label (e.g. a full
 * organization name) is cut off. Attach the full text as a native tooltip on
 * hover, but only when the text actually overflows - fully visible labels stay
 * tooltip-free. */
const applyOverflowTitle = (e: React.MouseEvent<HTMLElement>) => {
  const el = e.currentTarget;
  const full = el.textContent ?? "";
  if (el.scrollWidth > el.clientWidth) {
    if (el.title !== full) el.title = full;
  } else if (el.title) {
    el.removeAttribute("title");
  }
};

const VARIANT_CLASS = {
  pill: "border-border bg-card hover:border-indigo-400/60 focus-visible:border-indigo-500 focus-visible:ring-2 focus-visible:ring-indigo-500/20",
  form: "border-[#e2e8f0] dark:border-white/[0.07] bg-[#f5f7fb] dark:bg-white/[0.04] text-[#0f172a] dark:text-white hover:border-indigo-400/60 dark:hover:border-indigo-400/40 focus-visible:border-indigo-500 dark:focus-visible:border-indigo-500 focus-visible:ring-2 focus-visible:ring-indigo-500/10 w-full",
} as const;

/** Native title tooltip, but only when the text is actually clipped. */
const showTitleIfTruncated = (e: React.MouseEvent<HTMLElement>) => {
  const el = e.currentTarget;
  if (el.scrollWidth > el.clientWidth) {
    el.title = el.textContent ?? "";
  } else {
    el.removeAttribute("title");
  }
};

export function MultiSelectDropdown({
  onOpen,
  leadingLabel,
  options,
  selected,
  onChange,
  placeholder = "Select…",
  disabled = false,
  size = "md",
  className = "",
  searchable = true,
  onApply,
  applyLabel = "Apply",
  onClearAll,
  selectedNoun,
  variant = "pill",
  alwaysBelow = false,
  visibleOptions,
  loading = false,
  container,
  trigger,
  countBadge = false,
  selectAll = false,
  selectAllLabel = "Select All",
}: MultiSelectDropdownProps) {
  const [query, setQuery] = React.useState("");
  const triggerRef = React.useRef<HTMLButtonElement>(null);
  const [portalContainer, setPortalContainer] = React.useState<HTMLElement | null>(null);

  React.useEffect(() => {
    if (container !== undefined) {
      setPortalContainer(container);
      return;
    }
    if (triggerRef.current) {
      const dialog = triggerRef.current.closest<HTMLElement>(
        '[role="dialog"], [data-slot="sheet-content"]'
      );
      if (dialog) {
        setPortalContainer(dialog);
      }
    }
  }, [container]);

  const toggle = (v: string) =>
    onChange(selected.includes(v) ? selected.filter((x) => x !== v) : [...selected, v]);

  const filtered = query
    ? options.filter((o) => o.label.toLowerCase().includes(query.toLowerCase()))
    : options;

  /* Measured against the LISTED options, not every option, so the row
     reads correctly while a search narrows the list. */
  const allListedSelected =
    filtered.length > 0 && filtered.every((o) => selected.includes(o.value));
  const someListedSelected =
    !allListedSelected && filtered.some((o) => selected.includes(o.value));

  const toggleAll = () => {
    const listed = filtered.map((o) => o.value);
    if (allListedSelected) {
      const drop = new Set(listed);
      onChange(selected.filter((v) => !drop.has(v)));
    } else {
      onChange([...new Set([...selected, ...listed])]);
    }
  };

  /**
   * Select-all operates on what is VISIBLE, and ADDS to the selection rather
   * than replacing it. With a search active, "Select all" is read as "all of
   * these" -- and if it replaced the selection it would silently discard
   * everything picked outside the current query, which is a trap.
   */
  const allVisibleSelected =
    filtered.length > 0 && filtered.every((o) => selected.includes(o.value));

  const selectAllVisible = () =>
    onChange(Array.from(new Set([...selected, ...filtered.map((o) => o.value)])));

  /* What the trigger says at each size of selection.

     With countBadge on it says the useful thing at each: nothing picked
     shows the placeholder, ONE pick is named outright, and several become
     a number beside the label -- there is no room to name five
     applications and no honesty in naming only the first.

     It also sidesteps a sentence `selectedNoun` cannot get right at both
     ends. A fixed noun reads "1 applications Selected" at one pick and
     would read "3 application Selected" if it were singular instead. */
  const showBadge = countBadge && selected.length > 1;

  const summary =
    selected.length === 0
      ? placeholder
      : selected.length === 1 && (countBadge || !selectedNoun)
        ? options.find((o) => o.value === selected[0])?.label ?? "1 selected"
        : selectedNoun
          ? `${selected.length} ${selectedNoun} Selected`
          : `${selected.length} selected`;

  return (
    <PopoverPrimitive.Root
      onOpenChange={(o) => {
        if (!o) {
          setQuery("");
        } else {
          if (onOpen) onOpen();
          if (container === undefined && triggerRef.current) {
            const dialog = triggerRef.current.closest<HTMLElement>(
              '[role="dialog"], [data-slot="sheet-content"]'
            );
            if (dialog) setPortalContainer(dialog);
          }
        }
      }}
    >
      {trigger ? (
        <PopoverPrimitive.Trigger ref={triggerRef} asChild disabled={disabled}>
          {trigger}
        </PopoverPrimitive.Trigger>
      ) : (
      <PopoverPrimitive.Trigger
        ref={triggerRef}
        disabled={disabled}
        className={cn(
          "group inline-flex items-center justify-between gap-1.5 rounded-lg border text-foreground outline-none transition-colors disabled:cursor-not-allowed disabled:opacity-50",
          size === "sm" ? "h-8 px-2.5 text-xs min-w-0" : "h-[38px] px-3 text-sm min-w-[170px]",
          VARIANT_CLASS[variant],
          className
        )}
      >
        <span className="flex min-w-0 flex-1 items-center gap-1.5">
          {leadingLabel && (
            <span
              className={cn(
                "shrink-0 font-medium text-muted-foreground",
                size === "sm" ? "text-xs" : "text-sm",
              )}
            >
              {leadingLabel}
            </span>
          )}
          {showBadge ? (
            <span className="shrink-0 rounded-full bg-primary px-1.5 py-0.5 text-xs font-semibold tabular-nums text-primary-foreground">
              {selected.length}
            </span>
          ) : (
            <SelectedLabel
              label={summary}
              className={cn(
                "truncate font-semibold",
                selected.length ? "text-foreground" : "text-muted-foreground",
                size === "sm" ? "text-xs" : "text-sm",
              )}
              contentClassName="z-[300]"
            />
          )}
        </span>
       
        <ChevronDown
          className={cn(
            "shrink-0 text-muted-foreground transition-transform group-data-[state=open]:rotate-180",
            size === "sm" ? "size-3.5" : "size-4",
          )}
        />
      </PopoverPrimitive.Trigger>
      )}

      <PopoverPrimitive.Portal container={portalContainer || undefined}>
        <PopoverPrimitive.Content
          align="start"
          side="bottom"
          avoidCollisions={!alwaysBelow}
          sideOffset={6}
          onWheel={(e) => e.stopPropagation()}
          className={cn(
            "z-[200] min-w-[220px] overflow-hidden rounded-xl border border-border bg-popover text-popover-foreground shadow-xl data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0",
            // Matching the trigger width only makes sense for the built-in
            // trigger. A custom one (an icon button, say) would squeeze the
            // panel to nothing, so it sizes to its own content instead.
            !trigger && "w-[var(--radix-popover-trigger-width)]",
          )}
        >
          {searchable && (
            <div className="flex items-center gap-2 border-b border-border px-2.5 py-2">
              <Search className="size-3.5 shrink-0 text-muted-foreground" />
              <input
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Search…"
                className="w-full bg-transparent text-sm outline-none placeholder:text-muted-foreground"
              />
            </div>
          )}

          {/* Count + Clear all. Select-all deliberately does NOT live here —
              that is the `selectAll` checkbox row at the top of the list, and
              two controls doing the same job is what this row used to cause.
              Clear all stays because it is the only caller of `onClearAll`,
              which ExecutiveInsights and QuadrantDashboard both rely on to
              reset state of their own alongside the selection. */}
          {selected.length > 0 && (
            <div className="flex items-center justify-between border-b border-border px-2.5 py-1.5">
              <span className="text-[11px] font-medium text-muted-foreground">
                {selected.length} selected
              </span>
              <button
                type="button"
                onClick={() => {
                  onChange([]);
                  onClearAll?.();
                }}
                className="text-[11px] font-semibold text-indigo-600 hover:underline dark:text-indigo-400"
              >
                Clear all
              </button>
            </div>
          )}

          <div
            className="overflow-y-auto p-1"
            onWheel={(e) => e.stopPropagation()}
            style={{
              maxHeight: visibleOptions
                ? visibleOptions * OPTION_ROW_PX + LIST_PADDING_PX
                : DEFAULT_LIST_MAX_PX,
            }}
          >
            {loading ? (
              <p className="flex items-center justify-center gap-2 px-2.5 py-3 text-xs text-muted-foreground">
                <Loader2 className="size-3.5 animate-spin" />
                Loading
              </p>
            ) : filtered.length === 0 ? (
              <p className="px-2.5 py-3 text-center text-xs text-muted-foreground">No options</p>
            ) : (
              <>
                {selectAll && (
                  <button
                    type="button"
                    onClick={toggleAll}
                    className="mb-0.5 flex w-full items-center gap-2.5 rounded-lg border-b border-border px-2.5 py-1.5 text-left text-sm font-medium outline-none transition-colors hover:bg-indigo-50 dark:hover:bg-indigo-500/10"
                  >
                    <span
                      className={`flex size-4 shrink-0 items-center justify-center rounded border-2 transition-colors ${
                        allListedSelected || someListedSelected
                          ? "border-indigo-500 bg-indigo-500 text-white"
                          : "border-slate-400 dark:border-white/40"
                      }`}
                    >
                      {allListedSelected ? (
                        <Check className="size-3" />
                      ) : someListedSelected ? (
                        <Minus className="size-3" />
                      ) : null}
                    </span>
                    <span className="truncate">{selectAllLabel}</span>
                  </button>
                )}
                {filtered.map((opt) => {
                  const checked = selected.includes(opt.value);
                  return (
                    <button
                      key={opt.value}
                      type="button"
                      disabled={disabled || opt.disabled}
                      onClick={() => toggle(opt.value)}
                      className={cn(
                        "flex w-full items-center gap-2.5 rounded-lg px-2.5 py-1.5 text-left text-sm outline-none transition-colors hover:bg-indigo-50 dark:hover:bg-indigo-500/10",
                        opt.disabled && "cursor-not-allowed opacity-50 hover:bg-transparent",
                      )}
                    >
                      <span
                        className={`flex size-4 shrink-0 items-center justify-center rounded border-2 transition-colors ${
                          checked
                            ? "border-indigo-500 bg-indigo-500 text-white"
                            : "border-slate-400 dark:border-white/40"
                        }`}
                      >
                        {checked && <Check className="size-3" />}
                      </span>
                      {opt.dot && (
                        <span
                          className="size-2 shrink-0 rounded-full"
                          style={{ backgroundColor: opt.dot }}
                        />
                      )}
                      <SelectedLabel
                        label={opt.label}
                        className="truncate"
                        contentClassName="z-[300]"
                      />
                    </button>
                  );
                })}
              </>
            )}
          </div>

          {onApply && (
            <div className="border-t border-border p-2">
              <PopoverPrimitive.Close asChild>
                <button
                  type="button"
                  onClick={onApply}
                  className="w-full rounded-lg bg-indigo-600 px-3 py-1.5 text-xs font-semibold text-white transition-colors hover:bg-indigo-700"
                >
                  {applyLabel}
                </button>
              </PopoverPrimitive.Close>
            </div>
          )}
        </PopoverPrimitive.Content>
      </PopoverPrimitive.Portal>
    </PopoverPrimitive.Root>
  );
}
